import React, { useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';
import Points from 'components/Points';
import UsernameLink from 'components/UsernameLink/UsernameLink';
import {
  SuggestionsEmptyAllHandled,
  SuggestionsEmptyNoMatches,
} from 'screens/Main/tabs/TradeSuggestions/components/SuggestionsEmptyStates';
import {
  useGetTradeSuggestionsQuery,
  useValidateSuggestionMutation,
  useDismissSuggestionMutation,
} from 'store/services/tradeSuggestions/tradeSuggestionsApi';
import { TradeSuggestion, TradeSuggestionItem } from 'types/interfaces/tradeSuggestion';
import { getCardImageUrl } from 'utils/cardImageFallback';
import { getNetCopy } from 'utils/fundingCopy';
import {
  formatSuggestionUnavailableMessage,
  parseOfferError,
  SUGGESTION_GONE_MESSAGE,
} from 'utils/offerErrors';
import { buildSeedFromSuggestion, writeOfferBuilderSeed } from 'utils/offerBuilderSeed';

const STRIP_LIMIT = 4;

// The two empty states moved to TradeSuggestions/components/SuggestionsEmptyStates
// so the discovery surface and this strip render one definition rather than the
// page importing components out of this file. Re-exported for existing consumers;
// the copy is now the spec's Reference Copy verbatim.
export { SuggestionsEmptyAllHandled, SuggestionsEmptyNoMatches };

const sideArt = (items: TradeSuggestionItem[]) =>
  items.slice(0, 2).map((item) => (
    <img
      key={item.userMTGCardHaveId}
      className="offers-zero-sugg-art"
      src={getCardImageUrl({ image: item.card.imageUrl, scryfallId: item.card.scryfallId }, 'small')}
      alt={item.card.name}
      loading="lazy"
    />
  ));

const SuggestionMiniCard: React.FC<{ suggestion: TradeSuggestion }> = ({ suggestion }) => {
  const navigate = useNavigate();
  const [validateSuggestion] = useValidateSuggestionMutation();
  const [dismissSuggestion] = useDismissSuggestionMutation();
  const [isOpening, setIsOpening] = useState(false);
  const [staleMessage, setStaleMessage] = useState<string | null>(null);

  const { netLine, netAmount, netPrefix } = getNetCopy(
    suggestion.totals.youSendPoints,
    suggestion.totals.theySendPoints
  );

  // Validate before opening: a stale suggestion fails clearly at tap time
  // rather than seeding a broken offer (same message shape as the accept 409).
  const handleOpen = async () => {
    setIsOpening(true);
    try {
      const result = await validateSuggestion(suggestion.id).unwrap();
      if (!result.valid) {
        // A failed validate INVALIDATED the suggestion server-side and released
        // its rows; the tag invalidation below refetches the list to match.
        setStaleMessage(
          result.details
            ? formatSuggestionUnavailableMessage(result.details)
            : SUGGESTION_GONE_MESSAGE
        );
        return;
      }
      // Null means a have row vanished between the passing validate and now;
      // buildSeedFromSuggestion logs the anomaly. Show the stale message rather
      // than opening the builder with a partial bundle.
      const seed = buildSeedFromSuggestion(suggestion);
      if (!seed) {
        setStaleMessage(SUGGESTION_GONE_MESSAGE);
        return;
      }
      writeOfferBuilderSeed(seed);
      navigate(`/trade-offers/new/${suggestion.counterparty.username}`);
    } catch (err) {
      toast.error(parseOfferError(err).message);
    } finally {
      setIsOpening(false);
    }
  };

  const handleDismiss = async () => {
    try {
      await dismissSuggestion(suggestion.id).unwrap();
    } catch (err) {
      toast.error(parseOfferError(err).message);
    }
  };

  return (
    <article className="offers-zero-sugg-card">
      <header className="offers-zero-sugg-header">
        <UsernameLink
          username={suggestion.counterparty.username}
          isElite={suggestion.counterparty.isElite}
        />
        <span className="offers-zero-sugg-trust">
          {suggestion.counterparty.stats.rating != null
            ? `★ ${suggestion.counterparty.stats.rating.toFixed(1)} (${suggestion.counterparty.stats.ratingCount})`
            : 'No ratings yet'}
          {` · ${suggestion.counterparty.stats.cardsSent.toLocaleString()} cards sent`}
        </span>
      </header>
      <div className="offers-zero-sugg-sides">
        <div className="offers-zero-sugg-side">
          <span className="offer-summary-side-label offer-summary-side-label--they">
            THEY SEND
          </span>
          <div className="offers-zero-sugg-artrow">{sideArt(suggestion.theySend)}</div>
        </div>
        <div className="offers-zero-sugg-side">
          <span className="offer-summary-side-label offer-summary-side-label--you">YOU SEND</span>
          <div className="offers-zero-sugg-artrow">{sideArt(suggestion.youSend)}</div>
        </div>
      </div>
      <div className="offers-zero-sugg-net" aria-label={netLine}>
        {netAmount != null ? (
          <>
            <span>{netPrefix}</span>
            <Points type="small" points={netAmount} />
          </>
        ) : (
          netLine
        )}
      </div>
      {staleMessage ? (
        <div className="offers-zero-sugg-stale">{staleMessage}</div>
      ) : (
        <div className="offers-zero-sugg-actions">
          <button
            type="button"
            className="offers-review-btn"
            disabled={isOpening}
            onClick={handleOpen}
          >
            {isOpening ? 'Checking…' : 'Open ready-to-send offer'}
          </button>
          <button type="button" className="offers-zero-sugg-dismiss" onClick={handleDismiss}>
            Dismiss and free these cards for other traders
          </button>
        </div>
      )}
    </article>
  );
};

/**
 * Renders up to four proposable trades. If the endpoint is unavailable,
 * errors, or returns zero rows without an emptyReason, the strip omits itself
 * entirely — never a blank strip, never a spinner that never resolves, and a
 * suggestions failure never blanks the rest of the zero state.
 */
const SuggestionsStrip: React.FC = () => {
  const { data, isLoading, error } = useGetTradeSuggestionsQuery({ limit: STRIP_LIMIT });

  if (isLoading || error || !data) return null;

  if (data.suggestions.length === 0) {
    if (data.emptyReason === 'no_matches') {
      return (
        <section className="offers-zero-sugg" aria-label="Trade suggestions">
          <h2 className="offers-zero-block-heading">Trades you could propose</h2>
          <SuggestionsEmptyNoMatches />
        </section>
      );
    }
    if (data.emptyReason === 'all_handled') {
      return (
        <section className="offers-zero-sugg" aria-label="Trade suggestions">
          <h2 className="offers-zero-block-heading">Trades you could propose</h2>
          <SuggestionsEmptyAllHandled />
        </section>
      );
    }
    return null;
  }

  const count = data.suggestions.length;
  return (
    <section className="offers-zero-sugg" aria-label="Trade suggestions">
      <h2 className="offers-zero-block-heading">
        {count} {count === 1 ? 'trade' : 'trades'} you could propose right now
      </h2>
      <p className="offers-zero-block-subhead">
        Built from your want list and theirs. Both sides already priced, and one tap opens a
        ready-to-send offer.
      </p>
      <div className="offers-zero-sugg-grid">
        {data.suggestions.map((s) => (
          <SuggestionMiniCard key={s.id} suggestion={s} />
        ))}
      </div>
    </section>
  );
};

export default SuggestionsStrip;
